/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Plus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type ClinicUser, type ClinicUserStatus } from '../../lib/api/clinic-users.js';
import { useClinicUsers, useSetUserStatus } from '../../lib/queries/clinic-users.js';
import { authStore } from '../../lib/stores/auth-store.js';

import { InviteUserForm } from './invite-user-form.js';
import { RolesPanel } from './roles-panel.js';

const STATUS_TONE: Readonly<Record<ClinicUserStatus, BadgeTone>> = {
  invited: 'amber',
  active: 'green',
  disabled: 'slate',
};

const COLS = ['name', 'email', 'roles', 'status', 'lastLogin'] as const;

interface RowProps {
  readonly user: ClinicUser;
  readonly onToggle: (id: string, next: 'active' | 'disabled') => void;
  readonly disabled: boolean;
}

function UserRow({ user, onToggle, disabled }: RowProps): JSX.Element {
  const { t } = useTranslation();
  const next = user.status === 'disabled' ? 'active' : 'disabled';
  const toggle = useCallback(() => {
    onToggle(user.id, next);
  }, [onToggle, user.id, next]);
  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
      <td className="px-5 py-3 font-medium text-slate-800 dark:text-white">{user.fullName}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{user.email}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {user.roles.map((r) => t(`admin.users.role_${r}`)).join(', ')}
      </td>
      <td className="px-5 py-3">
        <Badge label={t(`admin.users.status_${user.status}`)} tone={STATUS_TONE[user.status]} />
      </td>
      <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
        {user.lastLoginAt !== null ? user.lastLoginAt.slice(0, 10) : '—'}
      </td>
      <td className="px-5 py-3 text-right">
        <Button variant="outline" size="sm" onClick={toggle} disabled={disabled}>
          {next === 'active' ? t('admin.users.enable') : t('admin.users.disable')}
        </Button>
      </td>
    </tr>
  );
}

/** Users tab — clinic-staff list + invite + enable/disable. */
function UsersTab(): JSX.Element {
  const { t } = useTranslation();
  const users = useClinicUsers();
  const setStatus = useSetUserStatus();
  const [open, setOpen] = useState(false);

  const list = users.data ?? [];
  const openModal = useCallback(() => {
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);
  const onToggle = useCallback(
    (id: string, next: 'active' | 'disabled') => {
      setStatus.mutate({ id, status: next });
    },
    [setStatus],
  );

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button onClick={openModal}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('admin.users.invite')}
        </Button>
      </div>
      <QueryState
        isLoading={users.isLoading}
        isError={users.isError}
        isEmpty={list.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('admin.users.empty')}
      >
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
              <tr>
                {COLS.map((c) => (
                  <th key={c} className="px-5 py-3 font-semibold">
                    {t(`admin.users.col.${c}`)}
                  </th>
                ))}
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  onToggle={onToggle}
                  disabled={setStatus.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={t('admin.users.inviteTitle')}
        description={t('admin.users.inviteSubtitle')}
        closeLabel={t('common.cancel')}
      >
        <InviteUserForm onDone={closeModal} />
      </Modal>
    </div>
  );
}

type UsersTabKey = 'users' | 'roles';

/** Admin → Users & Roles (Figma). Users tab always; Roles tab for Super Admin. */
export const ClinicUsersPage = observer(function ClinicUsersPage(): JSX.Element {
  const { t } = useTranslation();
  const [tab, setTab] = useState<UsersTabKey>('users');
  const canRoles = authStore.roles.includes('SUPER_ADMIN');
  const showUsers = useCallback(() => {
    setTab('users');
  }, []);
  const showRoles = useCallback(() => {
    setTab('roles');
  }, []);
  const tabClass = (active: boolean): string =>
    `focus-ring rounded-lg px-5 py-2 text-sm font-medium ${
      active ? 'bg-[var(--accent)] text-white' : 'text-slate-600 dark:text-slate-300'
    }`;

  return (
    <div className="p-8">
      <PageHeader title={t('admin.users.pageTitle')} subtitle={t('admin.users.pageSubtitle')} />
      {canRoles && (
        <div className="mb-6 inline-flex rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
          <button type="button" onClick={showUsers} className={tabClass(tab === 'users')}>
            {t('admin.users.tabUsers')}
          </button>
          <button type="button" onClick={showRoles} className={tabClass(tab === 'roles')}>
            {t('admin.users.tabRoles')}
          </button>
        </div>
      )}
      {canRoles && tab === 'roles' ? <RolesPanel /> : <UsersTab />}
    </div>
  );
});
